import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import { TrendingUp, Trophy, Medal, Award, Star, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PerformancePortalEmbed = ({ impersonatedUser, adminUser }) => {
  const [myScores, setMyScores] = useState([]);
  const [loading, setLoading] = useState(true);

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  useEffect(() => {
    fetchPerformanceData();
  }, [impersonatedUser]);

  const fetchPerformanceData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(
        `${API}/superadmin/impersonate/performance/${impersonatedUser.employee_id}`,
        config
      );
      
      setMyScores(response.data.scores || []);
    } catch (error) {
      console.error('Error fetching performance data:', error);
      toast.error('Unable to fetch performance data');
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (month) => months.find(m => m.value === month)?.label || '';

  const getRankBadge = (rank, total) => {
    if (rank === 1) return <Badge className="bg-yellow-500"><Trophy className="w-3 h-3 mr-1" />1st</Badge>;
    if (rank === 2) return <Badge className="bg-gray-400"><Medal className="w-3 h-3 mr-1" />2nd</Badge>;
    if (rank === 3) return <Badge className="bg-amber-600"><Award className="w-3 h-3 mr-1" />3rd</Badge>;
    return <Badge variant="outline">#{rank}</Badge>;
  };

  // Calculate average stats
  const averageStats = myScores.length > 0 ? {
    avgPercentage: Math.round(myScores.reduce((sum, s) => sum + (s.percentage || 0), 0) / myScores.length),
    avgScore: Math.round(myScores.reduce((sum, s) => sum + (s.total_score || 0), 0) / myScores.length),
    count: myScores.length
  } : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="performance-portal-embed">
      {/* Average Stats Card */}
      {averageStats && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Star className="w-5 h-5 text-yellow-500" />
              Performance Summary for {impersonatedUser.name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <p className="text-3xl font-bold text-blue-600">{averageStats.avgPercentage}%</p>
                <p className="text-sm text-gray-500">Average Percentage</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <p className="text-3xl font-bold text-purple-600">{averageStats.avgScore}</p>
                <p className="text-sm text-gray-500">Average Score</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <p className="text-3xl font-bold text-green-600">{averageStats.count}</p>
                <p className="text-sm text-gray-500">Evaluations</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Performance Cards */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Calendar className="w-5 h-5 text-blue-600" />
            Monthly Performance History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {myScores.length === 0 ? (
            <div className="text-center py-12">
              <TrendingUp className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No performance evaluations found</p>
              <p className="text-sm text-gray-400">Manager evaluations will appear here once submitted.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myScores.map(score => (
                <Card key={score.id} className="border-l-4 border-l-blue-500 hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center justify-between">
                      <span>{getMonthName(score.evaluation_month)} {score.evaluation_year}</span>
                      {getRankBadge(score.rank, score.total_employees)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 text-sm">Score</span>
                        <span className="font-bold">{score.total_score}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 text-sm">Percentage</span>
                        <span className="font-bold text-blue-600">{score.percentage}%</span>
                      </div>
                      <Progress value={score.percentage} className="h-2" />
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500">Rank</span>
                        <span className="font-medium">#{score.rank} of {score.total_employees}</span>
                      </div>
                      {score.kpp_scores && score.kpp_scores.length > 0 && (
                        <div className="border-t pt-3 mt-3">
                          <p className="text-xs text-gray-500 mb-2">KPP Breakdown:</p>
                          <div className="space-y-1">
                            {score.kpp_scores.slice(0, 4).map(kpp => (
                              <div key={kpp.kpp_id} className="flex justify-between text-xs">
                                <span className="text-gray-600 truncate max-w-[150px]">{kpp.kpp_name || kpp.kpp_id}</span>
                                <span className="font-medium">{kpp.actual_value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformancePortalEmbed;
